import * as React from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { ArrowUp, ArrowDown } from "lucide-react";

export interface ColumnSettingsValue<K extends string = string> {
  order: K[];
  visibility: Record<K, boolean>;
  tooltips: boolean;
}

export interface ColumnDef<K extends string = string> {
  key: K;
  label: string;
  tooltip?: string;
  lock?: boolean;
}

export interface ColumnPreset<K extends string = string> {
  id: string;
  name: string;
  settings: ColumnSettingsValue<K>;
  builtin?: boolean;
}

const normalize = <K extends string>(s: ColumnSettingsValue<K>, all: ColumnDef<K>[]): ColumnSettingsValue<K> => {
  const extra = all.map(c => c.key).filter(k => s.visibility[k] && !s.order.includes(k));
  return extra.length ? { ...s, order: [...s.order, ...extra] } : s;
};

interface ManageViewsModalProps<K extends string = string> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presets: ColumnPreset<K>[];
  activePresetId: string;
  allColumns: ColumnDef<K>[];
  systemDefault: ColumnSettingsValue<K>;
  onCommit: (nextPresets: ColumnPreset<K>[], activeId?: string) => void;
}

export default function TableViewsManager<K extends string = string>({
  open,
  onOpenChange,
  presets,
  activePresetId,
  allColumns,
  systemDefault,
  onCommit,
}: ManageViewsModalProps<K>) {
  const [selectedId, setSelectedId] = React.useState<string>(activePresetId);
  const selected = React.useMemo(() => presets.find(p => p.id === selectedId) ?? presets[0], [presets, selectedId]);
  const systemPresets = React.useMemo(() => presets.filter(p => p.builtin), [presets]);
  const userPresets = React.useMemo(() => presets.filter(p => !p.builtin), [presets]);

  const [name, setName] = React.useState<string>(selected?.name || "");
  const [local, setLocal] = React.useState<ColumnSettingsValue<K>>(selected?.settings || systemDefault);
  const [makeDefault, setMakeDefault] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState("");
  const [showVisibleOnly, setShowVisibleOnly] = React.useState(false);

  const isBuiltin = !!selected?.builtin;
  const dirty = React.useMemo(() => name !== selected?.name || JSON.stringify(local) !== JSON.stringify(selected?.settings), [name, local, selected]);

  React.useEffect(() => {
    if (!open) return;
    setName(selected?.name || "");
    setLocal(selected?.settings || systemDefault);
    setMakeDefault(false);
  }, [open, selected, systemDefault]);

  const orderedKeys = React.useMemo(() => Array.from(new Set([...(local.order || []), ...allColumns.map(c => c.key)])), [local.order, allColumns]);
  const visibleCount = React.useMemo(() => Object.values(local.visibility || {}).filter(Boolean).length, [local.visibility]);
  const columns = React.useMemo(() => {
    const colMap = new Map(allColumns.map(c => [c.key, c]));
    let arr = orderedKeys.map(k => colMap.get(k)!).filter(Boolean);
    if (search) arr = arr.filter(c => c.label.toLowerCase().includes(search.toLowerCase()));
    if (showVisibleOnly) arr = arr.filter(c => !!local.visibility[c.key]);
    return arr;
  }, [orderedKeys, allColumns, search, showVisibleOnly, local.visibility]);

  const onDragStart = (e: React.DragEvent<HTMLDivElement>, key: K) => { e.dataTransfer.setData("text/plain", String(key)); };
  const onDrop = (e: React.DragEvent<HTMLDivElement>, targetKey: K) => {
    const srcKey = e.dataTransfer.getData("text/plain") as K;
    setLocal(prev => {
      const order = [...prev.order];
      const from = order.indexOf(srcKey);
      const to = order.indexOf(targetKey);
      if (from === -1 || to === -1) return prev;
      order.splice(to, 0, ...order.splice(from, 1));
      return { ...prev, order };
    });
  };

  const toggleVis = (key: K, v: boolean) => setLocal(prev => {
    const visibility = { ...prev.visibility, [key]: v } as Record<K, boolean>;
    const order = v && !prev.order.includes(key) ? [...prev.order, key] : prev.order;
    return { ...prev, visibility, order };
  });

  const selectAll = (keys?: K[]) => setLocal(prev => {
    const list = (keys && keys.length ? keys : allColumns.map(c => c.key)) as K[];
    const visibility = { ...prev.visibility } as Record<K, boolean>;
    list.forEach(k => { visibility[k] = true; });
    return { ...prev, visibility } as ColumnSettingsValue<K>;
  });

  const clearAll = (keys?: K[]) => setLocal(prev => {
    const list = (keys && keys.length ? keys : allColumns.map(c => c.key)) as K[];
    const visibility = { ...prev.visibility } as Record<K, boolean>;
    list.forEach(k => { visibility[k] = false; });
    return { ...prev, visibility } as ColumnSettingsValue<K>;
  });

  const move = (key: K, dir: -1 | 1) => setLocal(prev => {
    const merged = Array.from(new Set([...(prev.order || []), ...allColumns.map(c => c.key) as K[]]));
    const idx = merged.indexOf(key);
    const nextIdx = idx + dir;
    if (idx === -1 || nextIdx < 0 || nextIdx >= merged.length) return prev;
    const order = [...merged];
    order.splice(nextIdx, 0, ...order.splice(idx, 1));
    return { ...prev, order };
  });

  const requestClose = (next: boolean) => {
    if (open && !next && dirty) {
      const ok = window.confirm("Discard unsaved changes?");
      if (!ok) return;
    }
    onOpenChange(next);
  };

  const handleSave = () => {
    const finalSettings = normalize(local, allColumns);
    if (selected?.builtin) {
      const newId = `user-${Date.now()}`;
      const duplicate: ColumnPreset<K> = { id: newId, name: name.trim() || `${selected.name} Copy`, settings: finalSettings };
      const next = [...presets, duplicate];
      onCommit(next, duplicate.id);
    } else {
      const updated: ColumnPreset<K> = { id: selected?.id || `user-${Date.now()}`, name: name.trim() || selected?.name || "Untitled", settings: finalSettings };
      const next = presets.map(p => (p.id === updated.id ? updated : p));
      onCommit(next, updated.id);
    }
    onOpenChange(false);
  };

  const handleNew = () => {
    const id = `user-${Date.now()}`;
    const blank: ColumnPreset<K> = { id, name: "New view", settings: systemDefault };
    const next = [...presets, blank];
    onCommit(next, id);
    setSelectedId(id);
    setName(blank.name);
    setLocal(blank.settings);
  };

  const handleDuplicate = () => {
    const id = `user-${Date.now()}`;
    const dup: ColumnPreset<K> = { id, name: `${selected?.name || "View"} Copy`, settings: local };
    const next = [...presets, dup];
    onCommit(next, id);
    setSelectedId(id);
    setName(dup.name);
  };

  const handleRename = () => {
    if (selected?.builtin) return;
    const newName = window.prompt("Rename view", name) || name;
    setName(newName);
    const next = presets.map(p => p.id === selected?.id ? { ...p, name: newName } : p);
    onCommit(next, selected?.id);
  };

  const handleDelete = () => {
    if (selected?.builtin) return;
    if (!window.confirm(`Delete view "${selected?.name}"?`)) return;
    const next = presets.filter(p => p.id !== selected?.id);
    onCommit(next, next[0]?.id);
    setSelectedId(next[0]?.id || "");
  };

  const resetToSystem = () => setLocal(systemDefault);

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent className="max-w-5xl md:max-w-6xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
          <DialogTitle>Manage Views</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-12 overflow-y-auto max-h-[60vh] pr-1 pb-24">
          <div className="md:col-span-4 space-y-3">
            <div className="space-y-2">
              <div className="font-medium">Views</div>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={handleNew}>New</Button>
                <Button size="sm" variant="outline" onClick={handleDuplicate}>Duplicate</Button>
                <Button size="sm" variant="ghost" onClick={handleRename} disabled={isBuiltin}>Rename</Button>
                <Button size="sm" variant="destructive" onClick={handleDelete} disabled={isBuiltin}>Delete</Button>
              </div>
            </div>
            <div className="rounded-md border max-h-[50vh] overflow-auto">
              {systemPresets.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground sticky top-0 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">System</div>
                  <div className="divide-y">
                    {systemPresets.map((p) => (
                      <button key={p.id} onClick={() => setSelectedId(p.id)} className={`flex w-full items-center justify-between px-3 py-2 text-left ${selectedId===p.id?"bg-muted/40":"hover:bg-muted/20"}`}>
                        <span className="truncate">{p.name} •</span>
                        <span className="text-xs text-muted-foreground">system</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {userPresets.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground sticky top-0 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">My views</div>
                  <div className="divide-y">
                    {userPresets.map((p) => (
                      <button key={p.id} onClick={() => setSelectedId(p.id)} className={`flex w-full items-center justify-between px-3 py-2 text-left ${selectedId===p.id?"bg-muted/40":"hover:bg-muted/20"}`}>
                        <span className="truncate">{p.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-8 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-muted-foreground">Preset name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} disabled={isBuiltin} />
              </div>
              <label className="mt-6 inline-flex items-center gap-2 text-sm">
                <Checkbox checked={makeDefault} onCheckedChange={(v:any)=>setMakeDefault(!!v)} />
                Set as my default view
              </label>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-medium">Columns <span className="ml-2 text-sm text-muted-foreground">({visibleCount} visible)</span></div>
              <div className="flex items-center gap-2">
                <label className="hidden sm:inline-flex items-center gap-2 text-sm text-muted-foreground"><Checkbox id="visible-only" checked={showVisibleOnly} onCheckedChange={(v:any)=>setShowVisibleOnly(!!v)} /> Visible only</label>
                <Input placeholder="Search columns" className="max-w-xs" value={search} onChange={(e)=>setSearch(e.target.value)} />
                <Button size="sm" variant="outline" onClick={()=>selectAll(columns.map(c=>c.key))}>Select all</Button>
                <Button size="sm" variant="ghost" onClick={()=>clearAll(columns.map(c=>c.key))}>Clear all</Button>
              </div>
            </div>
            <div className="rounded-md border bg-background divide-y max-h-[50vh] overflow-auto">
              {columns.map((col) => {
                const idx = orderedKeys.indexOf(col.key as K);
                const isFirst = idx <= 0;
                const isLast = idx >= orderedKeys.length - 1;
                return (
                  <div key={String(col.key)} className="flex items-center justify-between px-3 py-2 select-none hover:bg-muted/20 focus:outline-none focus:bg-muted/30" onClick={()=>toggleVis(col.key, !local.visibility[col.key])} role="button" tabIndex={0} onKeyDown={(e)=>{ if(e.key==='Enter' || e.key===' '){ e.preventDefault(); toggleVis(col.key, !local.visibility[col.key]); } }}>
                    <div className="flex items-center gap-3"><span className="text-sm">{col.label}</span></div>
                    <div className="flex items-center gap-1" onClick={(e)=>e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Move up" onClick={()=>move(col.key, -1)} disabled={isFirst || col.lock}><ArrowUp className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Move down" onClick={()=>move(col.key, 1)} disabled={isLast || col.lock}><ArrowDown className="h-4 w-4" /></Button>
                      <Checkbox checked={!!local.visibility[col.key]} onCheckedChange={(v:any)=>toggleVis(col.key, !!v)} disabled={col.lock} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <Separator className="my-2" />
        <DialogFooter>
          <div className="flex-1"><Button type="button" variant="ghost" onClick={resetToSystem}>Reset to system default</Button></div>
          <Button variant="outline" onClick={()=>requestClose(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!dirty}>Save Preset</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


